// How a character is carrying themselves — the Edge's only player-facing
// surface, and the whole of it.
//
// This is deliberately a small closed set of WORDS. There is no severity
// number on the wire, no percentage, no bar, no pip, and no second view:
// the rule that progression surfaces carry no numbers is not a discipline
// applied to the condition system, it is the condition system's only
// interface. A player learns they are in trouble the way a person does —
// by noticing, and by being told in words.
//
// It lives in shared rather than on the server for two reasons that pull
// the same way. The client renders the word and drives its own fog and
// vignette from it; and movement caps on it, so client prediction and the
// server tick reach the same speed by construction. A server-only slow
// would be authoritative and unspoofable and would also rubber-band —
// the client predicts with the unmodified stepPlayer and snaps at 1.5 m,
// so a cap the client cannot reproduce is a snap every fraction of a
// second, on the body of whoever is already having the worst time.
//
// The tokens converge at `failing` and `down` on purpose. Past a point the
// sufferer knows only that they are failing; knowing WHY is the Medic's
// product, and the wire must not give it away for free.

import { PLAYER_SPEED } from "./constants";

/** Every value `PlayerState.carriage` may hold. "" is fine and unmarked.
 *
 * The first pairs are the Edge's hazards (cold / fen). `clawed`/`mauled`
 * are what the wilds' teeth do; `shaken`/`rattled` are a warden's arc-lash.
 * All four ladders converge at `failing` exactly as the hazards always
 * have: past a point the sufferer knows only that they are failing, and
 * knowing why stays the Medic's product. */
export const CARRIAGE_TOKENS = [
  "",
  "chilled",
  "lightheaded",
  "clawed",
  "shaken",
  "numb",
  "reeling",
  "mauled",
  "rattled",
  "failing",
  "down",
] as const;

export type Carriage = (typeof CARRIAGE_TOKENS)[number];

export function isCarriage(v: unknown): v is Carriage {
  return typeof v === "string" && (CARRIAGE_TOKENS as readonly string[]).includes(v);
}

/**
 * Narrow a wire value to a Carriage, defaulting to unmarked.
 *
 * `PlayerState.carriage` is a plain schema string, so both sides go
 * through this rather than casting: an unrecognised token must read as
 * "nothing is wrong" and never as a crash or a speed of NaN.
 */
export function asCarriage(v: unknown): Carriage {
  return isCarriage(v) ? v : "";
}

/**
 * How far into trouble a token is, 0–4. Internal ordering only — never
 * rendered, never sent, never shown to a player. The client uses it to
 * pick a fog distance and a vignette strength, which are pictures rather
 * than numbers.
 */
export function carriageDepth(carriage: Carriage): 0 | 1 | 2 | 3 | 4 {
  switch (carriage) {
    case "":
      return 0;
    case "chilled":
    case "lightheaded":
    case "clawed":
    case "shaken":
      return 1;
    case "numb":
    case "reeling":
    case "mauled":
    case "rattled":
      return 2;
    case "failing":
      return 3;
    case "down":
      return 4;
  }
}

/**
 * The word a player reads. Present tense, second person implied, no
 * severity language borrowed from medicine — "you are numb" is a fact
 * about your hands, not a diagnosis.
 */
export function carriageWord(carriage: Carriage): string {
  switch (carriage) {
    case "":
      return "";
    case "chilled":
      return "chilled";
    case "lightheaded":
      return "light-headed";
    case "clawed":
      return "clawed";
    case "shaken":
      return "shaken";
    case "numb":
      return "numb";
    case "reeling":
      return "reeling";
    case "mauled":
      return "mauled";
    case "rattled":
      return "rattled";
    case "failing":
      return "failing";
    case "down":
      return "down";
  }
}

/**
 * The ceiling a body puts on its own gait, or undefined for no ceiling.
 *
 * This never fabricates failure and never makes an input lie (spec §7.7):
 * the stick still answers, every direction still works, and nothing is
 * randomly dropped. You are simply slower, in a way you can feel and
 * predict. Sprint stops answering at `numb`, which is the first thing a
 * cold body actually loses.
 */
export function carriageSpeedCap(carriage: Carriage): number | undefined {
  switch (carriage) {
    case "":
    case "chilled":
    case "lightheaded":
    case "clawed":
    case "shaken":
      return undefined;
    case "numb":
    case "reeling":
    case "mauled":
    case "rattled":
      return PLAYER_SPEED;
    case "failing":
      return FAILING_SPEED;
    case "down":
      return 0;
  }
}

/** A body that is failing can still walk itself somewhere, slowly. */
export const FAILING_SPEED = 1.1;

/** Down is down: you are on the ground until something changes. */
export function isDown(carriage: Carriage): boolean {
  return carriage === "down";
}
